import { fetchAuthSession, signOut } from "aws-amplify/auth";
import CONFIG from "../config";

async function getToken() {
  const method = sessionStorage.getItem("nova_sonic_auth_method");
  if (method === "midway") {
    return sessionStorage.getItem("nova_sonic_auth_id_token") || null;
  }
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

async function refreshMidwayTokensInline() {
  const refreshToken = sessionStorage.getItem("nova_sonic_auth_refresh_token");
  if (!refreshToken) throw new Error("No refresh token");

  const tokenUrl = `${CONFIG.cognito.domain}/oauth2/token`;
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: CONFIG.cognito.clientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!response.ok) throw new Error("Refresh failed");

  const tokens = await response.json();
  const expiresAt = Date.now() + tokens.expires_in * 1000;
  sessionStorage.setItem("nova_sonic_auth_access_token", tokens.access_token);
  sessionStorage.setItem("nova_sonic_auth_id_token", tokens.id_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem("nova_sonic_auth_refresh_token", tokens.refresh_token);
  }
  sessionStorage.setItem("nova_sonic_auth_expires_at", String(expiresAt));
}

async function handleForceSignOut() {
  const method = sessionStorage.getItem("nova_sonic_auth_method");
  if (method === "midway") {
    sessionStorage.removeItem("nova_sonic_auth_method");
    sessionStorage.removeItem("nova_sonic_auth_access_token");
    sessionStorage.removeItem("nova_sonic_auth_id_token");
    sessionStorage.removeItem("nova_sonic_auth_refresh_token");
    sessionStorage.removeItem("nova_sonic_auth_expires_at");
    window.location.href = `${CONFIG.cognito.domain}/logout?client_id=${CONFIG.cognito.clientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
  } else {
    try {
      await signOut();
    } catch {
      // ignore signOut errors
    }
  }
}

export async function apiFetch(endpoint, options = {}) {
  const token = await getToken();
  
  if (!token) {
    const error = new Error("Not authenticated");
    error.statusCode = 401;
    throw error;
  }

  const url = endpoint.startsWith("http") ? endpoint : `${CONFIG.apiUrl}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      "Authorization": "Bearer " + token,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (response.status === 401) {
    if (options._retried) {
      await handleForceSignOut();
      const error = new Error("Session expired");
      error.statusCode = 401;
      throw error;
    }

    const method = sessionStorage.getItem("nova_sonic_auth_method");

    try {
      if (method === "midway") {
        await refreshMidwayTokensInline();
      } else {
        await fetchAuthSession({ forceRefresh: true });
      }
      return apiFetch(endpoint, { ...options, _retried: true });
    } catch {
      await handleForceSignOut();
      const error = new Error("Session expired");
      error.statusCode = 401;
      throw error;
    }
  }

  if (response.status === 403) {
    let message = "Access denied";
    try {
      const body = await response.json();
      if (body.message) {
        message = body.message;
      }
    } catch {
      // use default message if body parsing fails
    }
    const error = new Error(message);
    error.statusCode = 403;
    throw error;
  }

  if (response.status === 404) return null;
  if (response.status === 204) return { success: true };
  
  if (response.status === 500) {
    const error = new Error("Server error");
    error.statusCode = 500;
    throw error;
  }

  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.message || data.error || "An error occurred");
    error.statusCode = response.status;
    throw error;
  }
  
  return data;
}

export default apiFetch;
